import type { Doc } from "building-types/doc";
import type { DocEntityType } from "building-types/doc/docEntityTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import {
  componentAssociatedEntityTypes,
  isComponentAssociatedEntityHandle,
} from "building-types/handles/docEntityHandleTypes";
import type { RWTable, Table } from "common/data/table";
import type { DocEntityReference } from "./entity/docEntityReferenceTypes";
import {
  docGetComponentAssociation,
  docHasComponentAssociation,
  docListComponentAssociationsForEntity,
  extractDocComponentAssociationReferences,
} from "./options/componentAssociations";

/**
 * Doc-level many-to-many association tables (rows with their own ids), as opposed to
 * hash join tables (handleHash → single parent id).
 *
 * Registered manually — association rows are a different schema construct than
 * fkHashJoinTable() and are not covered by the generated hash-join maps.
 */
export type DocAssociationTableName = "componentAssociations";

export interface DocAssociationTableRow {
  readonly id: string;
  readonly componentId: string;
}

export interface DocAssociationTableConfig {
  readonly tableName: DocAssociationTableName;
  /** Member / associated-entity side (today: OPTION_SET). */
  readonly isMemberHandle: (handle: DocEntityHandle) => boolean;
  /** Member entity types this table may reference. */
  readonly memberEntityTypes: readonly DocEntityType[];
  /** Container side entity type (today: COMPONENT). */
  readonly containerEntityType: DocEntityType;
  /** Find existing row for member + container. */
  readonly find: (doc: Doc, memberHandle: DocEntityHandle, containerId: string) => DocAssociationTableRow | undefined;
  /** All local rows for a member. */
  readonly listForMember: (doc: Doc, memberHandle: DocEntityHandle) => readonly DocAssociationTableRow[];
  /** Container id stored on a row. */
  readonly getContainerId: (row: DocAssociationTableRow) => string;
  /** Whether the source doc has an association between member and container. */
  readonly hasAssociation: (doc: Doc, memberHandle: DocEntityHandle, containerId: string) => boolean;
  /** Whether the container is mirrored from the same doc ref as the member. */
  readonly isContainerOwnedByMemberDocRef: (doc: Doc, memberHandle: DocEntityHandle, containerId: string) => boolean;
  /** Forward refs from member → containers (for FK extraction). */
  readonly extractMemberReferences: (doc: Doc, memberHandle: DocEntityHandle) => DocEntityReference[];
}

export const docAssociationTableConfigs: readonly DocAssociationTableConfig[] = [
  {
    tableName: "componentAssociations",
    isMemberHandle: isComponentAssociatedEntityHandle,
    memberEntityTypes: componentAssociatedEntityTypes,
    containerEntityType: "COMPONENT",
    find: (doc, memberHandle, containerId) => {
      if (!isComponentAssociatedEntityHandle(memberHandle)) {
        return undefined;
      }
      return docGetComponentAssociation(doc, memberHandle, containerId);
    },
    listForMember: (doc, memberHandle) => {
      if (!isComponentAssociatedEntityHandle(memberHandle)) {
        return [];
      }
      return docListComponentAssociationsForEntity(doc, memberHandle);
    },
    getContainerId: (row) => row.componentId,
    hasAssociation: (doc, memberHandle, containerId) => {
      if (!isComponentAssociatedEntityHandle(memberHandle)) {
        return false;
      }
      return docHasComponentAssociation(doc, memberHandle, containerId);
    },
    isContainerOwnedByMemberDocRef: (doc, memberHandle, containerId) => {
      if (!isComponentAssociatedEntityHandle(memberHandle)) {
        return false;
      }
      const containerRef = doc.entityRefs[containerId];
      if (!containerRef) {
        return false;
      }
      const memberRef = doc.entityRefs[memberHandle.id];
      return !!memberRef && memberRef.docRefId === containerRef.docRefId;
    },
    extractMemberReferences: (doc, memberHandle) => {
      if (!isComponentAssociatedEntityHandle(memberHandle)) {
        return [];
      }
      return extractDocComponentAssociationReferences(doc, memberHandle);
    },
  },
];

/** Container entity types registered by association tables — built once at module load. */
const associationContainerEntityTypes: ReadonlySet<DocEntityType> = new Set(
  docAssociationTableConfigs.map((config) => config.containerEntityType),
);

/** Cached member entity types per container type. */
const memberTypesByContainerType = new Map<DocEntityType, readonly DocEntityType[]>();

for (const config of docAssociationTableConfigs) {
  const existing = memberTypesByContainerType.get(config.containerEntityType);
  if (!existing) {
    memberTypesByContainerType.set(config.containerEntityType, [...config.memberEntityTypes]);
    continue;
  }
  const merged = new Set<DocEntityType>(existing);
  for (const entityType of config.memberEntityTypes) {
    merged.add(entityType);
  }
  memberTypesByContainerType.set(config.containerEntityType, Array.from(merged));
}

/** Ids of association-table container entities present in an entity-ref source set. */
export function associationContainerLookup(set: {
  readonly [hash: string]: { readonly handle: { readonly type: DocEntityType; readonly id: string } };
}): Table<true> {
  const included: RWTable<true> = {};

  for (const hash in set) {
    const source = set[hash];
    if (!associationContainerEntityTypes.has(source.handle.type)) {
      continue;
    }
    included[source.handle.id] = true;
  }

  return included;
}

/** Forward association-table references for a member entity (doc-ref dependency traversal). */
export function extractDocAssociationTableReferences(doc: Doc, memberHandle: DocEntityHandle): DocEntityReference[] {
  const references: DocEntityReference[] = [];

  for (const config of docAssociationTableConfigs) {
    if (!config.isMemberHandle(memberHandle)) {
      continue;
    }
    const memberReferences = config.extractMemberReferences(doc, memberHandle);
    for (const reference of memberReferences) {
      references.push(reference);
    }
  }

  return references;
}

/** Member entity types that can associate with a given container type via association tables. */
export function docAssociationTableMemberTypesForContainer(
  containerEntityType: DocEntityType,
): readonly DocEntityType[] {
  return memberTypesByContainerType.get(containerEntityType) ?? [];
}

/** Whether any registered association table links memberHandle to containerId. */
export function docHasAssociationTableMembership(
  doc: Doc,
  memberHandle: DocEntityHandle,
  containerEntityType: DocEntityType,
  containerId: string,
): boolean {
  for (const config of docAssociationTableConfigs) {
    if (config.containerEntityType !== containerEntityType) {
      continue;
    }
    if (!config.isMemberHandle(memberHandle)) {
      continue;
    }
    if (config.hasAssociation(doc, memberHandle, containerId)) {
      return true;
    }
  }
  return false;
}
